import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CONSULTANT_LOCK } from '@/constants/testIds';
import { Lock, CheckCircle2, Circle, ArrowRight } from 'lucide-react';

const CHECKLIST_STEPS = [
  { key: 'booking_scheduled', label: 'Initial pre-assessment booking scheduled' },
  { key: 'deposit_paid', label: 'Initial deposit received by All Build Consult team' },
  { key: 'report_issued', label: 'Initial report/recommendation issued by team' },
  { key: 'next_stage_confirmed', label: 'Next stage of report or recommendation confirmed' },
  { key: 'next_stage_booking_scheduled', label: 'Next stage booking scheduled' },
  { key: 'next_stage_deposit_paid', label: 'Next stage deposit paid' }
];

const SPECIALIST_REFERRAL_TYPES = [
  {
    name: 'Roofing Reports',
    description: 'Referral & summary review of independent roofing specialist reports — defects, leaks, structural integrity, and rectification advice.'
  },
  {
    name: 'Plumbing Leak Test & Reports',
    description: 'Referral & summary review of plumbing leak-detection tests and licensed plumber reports — water ingress, pressure tests, and drainage findings.'
  },
  {
    name: 'Electrical & Solar Reports',
    description: 'Referral & summary review of licensed electrical and solar specialist reports — safety, compliance, and performance findings.'
  },
  {
    name: 'Asbestos Sampling & Reports',
    description: 'Referral & summary review of NATA-accredited asbestos sampling and initial assessment reports — identification, risk and removal advice.'
  },
  {
    name: 'Building Reports',
    description: 'Referral & summary review of independent building reports — pre-purchase, dilapidation, handover, and defect inspections.'
  }
];

export default function LockedConsultants({ stagesCompleted, servicesCatalog }) {
  const navigate = useNavigate();

  // Memoise category grouping + sorted keys so they don't recompute on every render
  const groupedCategories = useMemo(() => {
    const grouped = servicesCatalog.reduce((acc, s) => {
      if (!acc[s.category]) acc[s.category] = [];
      acc[s.category].push(s);
      return acc;
    }, {});
    return {
      grouped,
      sortedKeys: Object.keys(grouped).sort()
    };
  }, [servicesCatalog]);
  const { grouped, sortedKeys } = groupedCategories;

  return (
    <div data-testid={CONSULTANT_LOCK.lockScreen} className="space-y-6">
      {/* Lock banner */}
      <div className="grid-border bg-white p-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
            <Lock className="w-6 h-6 text-slate-700" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
              Expert Consultants — Access Locked
            </h2>
            <p className="text-slate-600">
              External expert consultants become available once the All Build Consult team has completed
              your initial assessment workflow. This ensures every consultant referral is informed by a verified
              report and recommendation from our in-house team.
            </p>
          </div>
        </div>

        {/* Progress checklist */}
        <div className="grid-border bg-slate-50 p-6 mb-4">
          <h3 className="text-sm font-semibold text-black mb-4 uppercase tracking-wide">
            Your Progress
          </h3>
          <ul className="space-y-3">
            {CHECKLIST_STEPS.map((step, idx) => {
              const completed = stagesCompleted[step.key];
              const isPrevComplete = idx === 0 || stagesCompleted[CHECKLIST_STEPS[idx - 1].key];
              const isCurrent = !completed && isPrevComplete;
              return (
                <li
                  key={step.key}
                  data-testid={CONSULTANT_LOCK.progressItem}
                  className="flex items-start gap-3"
                >
                  {completed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Circle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isCurrent ? 'text-blue-600' : 'text-slate-300'}`} />
                  )}
                  <div className="flex-1">
                    <span className={`text-sm ${completed ? 'text-slate-500 line-through' : isCurrent ? 'text-black font-medium' : 'text-slate-500'}`}>
                      {step.label}
                    </span>
                    {isCurrent && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600">
                        <ArrowRight className="w-3 h-3" />
                        Next step
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => navigate('/bookings')}
            className="px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all flex items-center gap-2 font-medium"
          >
            Continue to Bookings
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Service catalog with deposit amounts */}
      <div data-testid={CONSULTANT_LOCK.servicesCatalog} className="grid-border bg-white p-8">
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
          Schedule of Reporting Services
        </h3>
        <p className="text-sm text-slate-600 mb-6">
          All Build Consult team service tiers. Deposit is paid up front; balance is due upon report delivery.
        </p>

        <div className="space-y-6">
          {sortedKeys.map((category) => {
            const isComingSoon = category === 'Trade Works & Administration';
            return (
            <div key={category} data-testid={isComingSoon ? 'schedule-coming-soon-section' : undefined}>
              <h4 className="text-sm font-semibold text-black uppercase tracking-wide mb-3 pb-2 border-b border-slate-200 flex items-center gap-2 flex-wrap">
                <span>
                  {category === 'Specialist Services'
                    ? 'Specialist Services — Referral & Report Explanation'
                    : category}
                </span>
                {isComingSoon && (
                  <span
                    data-testid="schedule-coming-soon-badge"
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider rounded-sm normal-case"
                  >
                    Coming Soon
                  </span>
                )}
              </h4>
              {isComingSoon && (
                <div
                  data-testid="trade-works-coming-soon-banner"
                  className="mb-4 p-3 bg-red-50 border-l-2 border-red-600 rounded-sm text-xs text-red-900"
                >
                  <strong className="text-red-700">Coming Soon —</strong> Trade Works &amp;
                  Administration services are launching soon. Final pricing, scope and
                  inclusions will be confirmed by the All Build Consult team prior to release.
                </div>
              )}
              {category === 'Specialist Services' ? (
                <div data-testid="specialist-services-section" className="grid md:grid-cols-2 gap-3">
                  {SPECIALIST_REFERRAL_TYPES.map((item) => (
                    <div
                      key={item.name}
                      data-testid="specialist-report-card"
                      className="grid-border bg-slate-50 p-4"
                    >
                      <h5 className="font-semibold text-black mb-1">{item.name}</h5>
                      <p className="text-xs text-slate-600">{item.description}</p>
                    </div>
                  ))}
                  <div
                    data-testid="specialist-services-note"
                    className="md:col-span-2 grid-border bg-amber-50 border-amber-200 p-4"
                  >
                    <div className="flex items-start gap-2">
                      <Lock className="w-4 h-4 text-amber-800 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-slate-700 leading-relaxed">
                        <strong className="text-amber-900">To be noted —</strong> All specialist services
                        referral &amp; reports listed above are based on <strong>referral and explanation
                        only</strong>. Our cost is therefore purely a <strong>referral and review service</strong>.
                        Once a deposit is made and the specialist report is requested, we can provide a
                        simpler &amp; summary review once the available specialist report is received.
                        These specialist reports are <strong>not subject to any lead time</strong> and can
                        only be advised once we confirm availability with our key external consultants.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`grid md:grid-cols-2 gap-3 ${isComingSoon ? 'opacity-60' : ''}`}>
                  {grouped[category].map((service) => (
                    <div
                      key={service.id}
                      data-testid={
                        isComingSoon
                          ? 'trade-works-service-card'
                          : service.requires_workflow_complete
                            ? 'gated-service-card'
                            : undefined
                      }
                      className={`grid-border p-4 ${
                        isComingSoon
                          ? 'bg-slate-100 border-red-200 ring-1 ring-red-100'
                          : service.requires_workflow_complete
                            ? 'bg-amber-50 border-amber-200'
                            : 'bg-slate-50'
                      }`}
                    >
                      <div className="flex justify-between items-start gap-3 mb-2">
                        <div className="min-w-0">
                          <h5 className={`font-semibold ${isComingSoon ? 'text-slate-500' : 'text-black'}`}>
                            {service.name}
                          </h5>
                          {service.requires_workflow_complete && !isComingSoon && (
                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide rounded-sm">
                              <Lock className="w-3 h-3" />
                              Available after initial report
                            </span>
                          )}
                          {isComingSoon && (
                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-red-100 text-red-800 text-[10px] font-semibold uppercase tracking-wide rounded-sm">
                              Coming Soon
                            </span>
                          )}
                        </div>
                        {!isComingSoon && (
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs text-slate-500">Deposit (AUD)</div>
                            <div className="font-bold text-blue-600 flex items-baseline gap-0.5">
                              <span className="text-sm">A$</span>
                              <span>{service.deposit_amount.toFixed(0)}</span>
                            </div>
                            {service.full_price > 0 &&
                              Math.abs(service.deposit_amount / service.full_price - 0.5) < 0.01 && (
                                <div data-testid="deposit-50pct-hint" className="text-[10px] text-slate-500 mt-0.5">
                                  (50% of total)
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                      <p className={`text-xs mb-2 ${isComingSoon ? 'text-slate-500' : 'text-slate-600'}`}>
                        {service.description}
                      </p>
                      {!isComingSoon && (
                        <div className="text-xs text-slate-500">
                          Full price: <span className="font-medium text-black">A${service.full_price.toFixed(0)}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
